import type {
  ActivityCategory,
  ActivityEventDto,
  ActivityEventType,
  ActivityFeedDto,
  AttachSiteImageDto,
  AttachTourImageDto,
  AttachTourMediaDto,
  CreatePlaceDto,
  CreateUploadUrlDto,
  MediaPurpose,
  PlaceDto,
  PublishChecklistDto,
  PublishChecklistItemDto,
  ReorderSiteImagesDto,
  ReorderTourImagesDto,
  SetLiveOnWebsiteDto,
  SiteImageDto,
  SiteImageSlot,
  TourImageDto,
  TourMediaRefDto,
  TourWebsiteDto,
  UpdatePlaceDto,
  UpdateSiteImageDto,
  UpdateTourImageDto,
  UpdateTourSlugDto,
  UpdateTourWebsiteDto,
  UpdateWebsiteSettingsDto,
  UploadUrlDto,
  WebsiteCategory,
  WebsiteSettingsDto,
  AddLeadActivityDto,
  AvailableActivityMonthDto,
  AvailableReportMonthDto,
  AddExpensePaymentDto,
  BookingDeletionPreviewDto,
  BookingDto,
  BookingEditLogEntryDto,
  BookingProfitDto,
  CancelBookingDto,
  CancelBookingResultDto,
  ChangeBookingCustomerDto,
  ConvertLeadResultDto,
  CreateBookingDto,
  CreateCreditNoteDto,
  CreateExpenseDto,
  CreateUserDto,
  DepartureProfitDto,
  ExpenseDto,
  ExpenseListDto,
  ExpenseListFiltersDto,
  ExpenseSummaryDto,
  PayablesAgingDto,
  ProfitBasis,
  UpdateExpenseDto,
  VoidExpenseDto,
  UpdateUserDto,
  DeletedBookingArchiveDto,
  DeleteBookingDto,
  DeleteBookingResultDto,
  CreateCustomerDto,
  CreateDepartureDto,
  CreateLeadDto,
  CreateTourDto,
  ItineraryDayInputDto,
  LeadActivityDto,
  LinkVendorDto,
  PricingTierInputDto,
  UpdateDepartureDto,
  RecordPaymentDto,
  ReviseBookingValueDto,
  UpdateBookingStatusDto,
  UpdateCustomerDto,
  UpdateLeadDto,
  UpdateTourDto,
  CreditNoteDto,
  CustomerDto,
  DashboardMonthlyOverviewDto,
  DashboardSummaryDto,
  DocumentEditLogEntryDto,
  EditBookingDto,
  EditBookingPreviewDto,
  EditBookingResultDto,
  GlobalSearchResultsDto,
  InvoiceDto,
  InvoiceSettingsDto,
  InvoiceSummaryDto,
  LeadDto,
  PaymentDto,
  PaymentLedgerDto,
  PaymentSummaryDto,
  ReceiptDto,
  UpdateInvoiceDto,
  UpdateInvoiceSettingsDto,
  UpdateVoucherDto,
  VoucherDto,
  PermissionCellUpdateDto,
  ReportsSummaryDto,
  PermissionMatrixDto,
  AddBookingNoteDto,
  DeletedTourDto,
  MarkAllReadResultDto,
  MarkEventReadResultDto,
  NotificationDto,
  NotificationListDto,
  TeamMemberDto,
  TourDto,
  VoidPaymentDto,
  VoidPaymentResultDto,
  VoucherWithInvoiceDto,
} from '@gty/common';

import {
  addBookingNote,
  bounceChequePayment,
  clearChequePayment,
  voidPayment,
  fetchNotifications,
  markAllNotificationsRead,
  fetchActivityFeed,
  fetchActivityMonths,
  markActivityEventRead,
  fetchBooking,
  fetchBookings,
  addLeadActivity,
  convertLead,
  createBooking,
  createCustomer,
  createLead,
  createTour,
  createCreditNote,
  deleteTour,
  fetchCreditNote,
  fetchCreditNotes,
  fetchLeadActivities,
  fetchCustomer,
  fetchCustomers,
  fetchDashboardMonths,
  fetchDashboardSummary,
  fetchInvoice,
  fetchInvoiceSettings,
  fetchInvoiceSummary,
  fetchInvoices,
  fetchLead,
  fetchLeads,
  fetchPaymentSummary,
  fetchPaymentLedger,
  fetchPayments,
  fetchPaymentsForCustomer,
  fetchReceipt,
  fetchInvoiceForBooking,
  fetchInvoiceEdits,
  fetchVoucherForBooking,
  fetchVoucherEdits,
  updateInvoice,
  updateInvoiceSettings,
  uploadInvoiceQrImage,
  createMediaUploadUrl,
  updateVoucher,
  fetchPermissionMatrix,
  fetchReportsSummary,
  fetchReportMonths,
  fetchMonthlyOverview,
  createUser,
  fetchTeam,
  updateUser,
  fetchTour,
  fetchTours,
  fetchVoucher,
  fetchVouchers,
  addDeparture,
  linkVendor,
  recordPayment,
  removeDeparture,
  removeTourBrochure,
  uploadTourBrochure,
  replaceItinerary,
  replaceTiers,
  unlinkVendor,
  updateTourWebsite,
  isTourSlugAvailable,
  updateTourSlug,
  setTourCoverImage,
  removeTourCoverImage,
  addTourGalleryImage,
  removeTourGalleryImage,
  reorderTourGalleryImages,
  updateTourGalleryImage,
  setTourWebsiteBrochure,
  removeTourWebsiteBrochure,
  fetchTourPublishChecklist,
  setTourLiveOnWebsite,
  setTourPlaces,
  fetchPlaces,
  fetchPlace,
  createPlace,
  updatePlace,
  reorderPlaces,
  setPlaceImage,
  removePlaceImage,
  deletePlace,
  fetchSiteImages,
  attachSiteImage,
  updateSiteImage,
  removeSiteImage,
  reorderSiteImages,
  fetchWebsiteSettings,
  updateWebsiteSettings,
  updateBookingStatus,
  reviseBookingValue,
  cancelBooking,
  previewEditBooking,
  editBooking,
  getBookingEditHistory,
  changeBookingCustomer,
  getBookingDeletionPreview,
  deleteBooking,
  listDeletedBookings,
  fetchExpenses,
  fetchExpense,
  fetchExpenseSummary,
  fetchPayablesAging,
  fetchVendorSuggestions,
  createExpense,
  updateExpense,
  voidExpense,
  addExpensePayment,
  deleteExpensePayment,
  fetchBookingProfit,
  fetchDepartureProfit,
  updateDeparture,
  updateCustomer,
  updateLead,
  updatePermissionMatrix,
  updateTour,
  searchGlobal,
  createShareLink,
  fetchShareLinks,
  revokeShareLink,
  type ActivityFeedQuery,
} from '../api/endpoints';
import {
  downloadTourBrochure,
  viewTourBrochure,
  type BrochureViewOutcome,
  downloadMonthlyReport,
  downloadMonthlyOverview,
  downloadExpensesExport,
} from '../api/download';
import { isApiError } from '../api/errors';
import { EXPENSES_DEMO_ENABLED } from '../expenses/demo-flag';
import type {
  ShareDocumentKind,
  ShareLinkDto,
  ShareLinkSummaryDto,
} from '../documents/share-link';
import type { ChequeActionRequestDto } from '../payments/cheque-actions';
import type { IconName } from '../ui/icons';
import { DASHBOARD_MOCK, DASHBOARD_MONTHS_MOCK } from './dashboard.mock';

/**
 * THE service boundary (spec §7.4).
 *
 * Components import from `services` and nowhere else. Every module below is now
 * backed by a real endpoint over Prisma — the per-module `*.mock.ts` fixtures
 * are gone, and their data lives in `packages/back/prisma/fixtures/`, where the
 * seeder writes it to Postgres. There is one copy of it, and `tsc` checks that
 * copy against the shared DTOs.
 */

/**
 * `VITE_USE_MOCK_DASHBOARD` defaults to FALSE (real data) when unset.
 *
 * NOW ONLY AFFECTS THE DASHBOARD. Every other module talks to the API
 * unconditionally: keeping a second in-browser copy of leads, tours and
 * invoices was how the two versions drifted in the first place.
 *
 * This is opt-IN, not opt-OUT: the flag must be explicitly set to `'true'` to
 * render mock numbers. An unset/misconfigured env var (very plausible on a
 * deployment nobody has explicitly configured) now fails safe to REAL data,
 * never silently back to fixtures. The dashboard's own GET
 * /api/dashboard/summary endpoint is real (Prisma-backed) as of this fix —
 * the mock path here exists only for local/demo use.
 */
export const USING_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DASHBOARD === 'true';

/** Simulated latency, so loading skeletons still render in mock mode. */
function delayed<T>(value: T, ms = 220): Promise<T> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(value), ms);
  });
}

/**
 * 404 -> `null`, everything else rethrown.
 *
 * The detail screens distinguish "no such record" (render the empty state) from
 * "the request failed" (render the error state). Collapsing every rejection to
 * null would show a reassuring "not found" for what is actually a dead API.
 */
async function orNullOn404<T>(request: Promise<T>): Promise<T | null> {
  try {
    return await request;
  } catch (error: unknown) {
    if (isApiError(error) && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/* ── contract types, re-exported under the names the screens already use ──── */

export type LeadRecord = LeadDto;
export type CustomerRecord = CustomerDto;
export type TourRecord = TourDto;
export type BookingRecord = BookingDto;
export type InvoiceRecord = InvoiceDto;
export type VoucherWithInvoice = VoucherWithInvoiceDto;

export type {
  BookingComponentDto as BookingComponent,
  BookingLifecycle,
  BookingTrailEntryDto as BookingTrailEntry,
  CancellationRowDto as CancellationRow,
  CommunicationEntryDto as CommunicationEntry,
  CustomerBookingDto as CustomerBooking,
  CustomerDocumentDto as CustomerDocument,
  DepartureStatus,
  InvoiceHotelDto as InvoiceHotel,
  InvoiceLineItemDto,
  InvoicePassengerDto as InvoicePassenger,
  InvoiceStatus,
  InvoiceSummaryDto,
  InvoiceTransportDto as InvoiceTransport,
  ItineraryDayDto as ItineraryDay,
  LinkedVendorDto as LinkedVendor,
  PassengerDto as Passenger,
  PaymentState,
  PricingTierDto as PricingTier,
  TourCategory,
  TourDepartureDto as TourDeparture,
  VoucherDto as VoucherRecord,
} from '@gty/common';

/**
 * The customer screens label a trip's state with this union.
 *
 * Aliased rather than redeclared so it stays tied to `BookingLifecycle`; the
 * old local `BookingStatus` was a second, silently-diverging copy.
 */
export type { BookingLifecycle as BookingStatus } from '@gty/common';

export { TOUR_CATEGORIES } from '@gty/common';

/* ── dashboard ─────────────────────────────────────────────────────────────── */

export interface DashboardService {
  getSummary: (signal?: AbortSignal) => Promise<DashboardSummaryDto>;
  getMonths: (signal?: AbortSignal) => Promise<DashboardMonthlyOverviewDto>;
}

export const dashboardService: DashboardService = USING_MOCK_DATA
  ? { getSummary: () => delayed(DASHBOARD_MOCK), getMonths: () => delayed(DASHBOARD_MONTHS_MOCK) }
  : { getSummary: (signal) => fetchDashboardSummary(signal), getMonths: (signal) => fetchDashboardMonths(signal) };

/* ── leads ─────────────────────────────────────────────────────────────────── */

export type { CreateLeadDto, UpdateLeadDto, LeadActivityDto, ConvertLeadResultDto } from '@gty/common';

export interface LeadsService {
  list: (signal?: AbortSignal) => Promise<readonly LeadRecord[]>;
  get: (id: string, signal?: AbortSignal) => Promise<LeadRecord | null>;
  activities: (id: string, signal?: AbortSignal) => Promise<readonly LeadActivityDto[]>;
  create: (payload: CreateLeadDto) => Promise<LeadRecord>;
  update: (id: string, payload: UpdateLeadDto) => Promise<LeadRecord>;
  addActivity: (id: string, payload: AddLeadActivityDto) => Promise<LeadActivityDto>;
  /** Creates the Customer, copies the timeline, marks the lead converted. */
  convert: (id: string) => Promise<ConvertLeadResultDto>;
}

export const leadsService: LeadsService = {
  list: (signal) => fetchLeads(signal),
  get: (id, signal) => orNullOn404(fetchLead(id, signal)),
  activities: (id, signal) => fetchLeadActivities(id, signal),
  create: (payload) => createLead(payload),
  update: (id, payload) => updateLead(id, payload),
  addActivity: (id, payload) => addLeadActivity(id, payload),
  convert: (id) => convertLead(id),
};

/* ── customers ─────────────────────────────────────────────────────────────── */

export type { CreateCustomerDto, UpdateCustomerDto } from '@gty/common';

export interface CustomersService {
  list: (signal?: AbortSignal) => Promise<readonly CustomerRecord[]>;
  get: (id: string, signal?: AbortSignal) => Promise<CustomerRecord | null>;
  create: (payload: CreateCustomerDto) => Promise<CustomerRecord>;
  update: (id: string, payload: UpdateCustomerDto) => Promise<CustomerRecord>;
}

export const customersService: CustomersService = {
  list: (signal) => fetchCustomers(signal),
  get: (id, signal) => orNullOn404(fetchCustomer(id, signal)),
  create: (payload) => createCustomer(payload),
  update: (id, payload) => updateCustomer(id, payload),
};

/* ── tours ─────────────────────────────────────────────────────────────────── */

export type { CreateTourDto, UpdateTourDto } from '@gty/common';

export type {
  CreateDepartureDto,
  ItineraryDayInputDto,
  LinkVendorDto,
  PricingTierInputDto,
  UpdateDepartureDto,
} from '@gty/common';

/** Re-exported so the brochure UI never has to reach past this boundary. */
export type { BrochureViewOutcome } from '../api/download';

export interface ToursService {
  list: (signal?: AbortSignal) => Promise<readonly TourRecord[]>;
  get: (id: string, signal?: AbortSignal) => Promise<TourRecord | null>;
  create: (payload: CreateTourDto) => Promise<TourRecord>;
  update: (id: string, payload: UpdateTourDto) => Promise<TourRecord>;
  /**
   * Retire a tour. Owner/Admin only — enforced by the server's TOUR_MASTER:FULL
   * check, not by hiding the button; the button is hidden as well, but that is
   * ergonomics, not security.
   */
  remove: (id: string) => Promise<DeletedTourDto>;
  /** Itinerary and tiers are replaced whole — day numbers and order matter. */
  replaceItinerary: (id: string, days: ItineraryDayInputDto[]) => Promise<TourRecord>;
  replaceTiers: (id: string, tiers: PricingTierInputDto[]) => Promise<TourRecord>;
  addDeparture: (id: string, payload: CreateDepartureDto) => Promise<TourRecord>;
  updateDeparture: (
    id: string,
    departureId: string,
    payload: UpdateDepartureDto,
  ) => Promise<TourRecord>;
  removeDeparture: (id: string, departureId: string) => Promise<TourRecord>;
  linkVendor: (id: string, payload: LinkVendorDto) => Promise<TourRecord>;
  unlinkVendor: (id: string, vendorId: string) => Promise<TourRecord>;
  /**
   * Attach or replace the customer-facing brochure PDF.
   *
   * Separate from `update` because it is a different request shape (multipart)
   * with failure modes a text field does not have — wrong type, too big, bytes
   * that do not match their declared type. Folding it into the tour form would
   * mean one Save that either does or does not carry a file, and one error slot
   * for two unrelated kinds of rejection. Same reasoning as
   * `settingsService.uploadQrImage`.
   *
   * Resolves to the refreshed tour, so the caller renders the SERVER's filename
   * — which is not always the one the user picked; see `uploadTourBrochure`.
   */
  uploadBrochure: (id: string, file: File) => Promise<TourRecord>;
  /** Clear it. Resolves to nothing — refresh the tour, do not read this reply. */
  removeBrochure: (id: string) => Promise<void>;
  /**
   * Open the brochure in a new tab / save it to disk.
   *
   * BOTH GO THROUGH `api/download`, NEVER A LINK. `GET /tours/:id/brochure` is
   * bearer-authenticated, and an `<a href>` or a `window.open(url)` sends no
   * `Authorization` header — it 401s for a signed-in user. These delegate to the
   * fetch-then-blob-URL implementation that exists for exactly that reason.
   *
   * They are surfaced HERE, unlike the document PDF downloads (which their
   * screens import from `api/download` directly), so the tour detail screen has
   * one import for every brochure action rather than two boundaries for four
   * calls against one route.
   *
   * `view` must be invoked DIRECTLY from the click handler — it opens the tab
   * synchronously to stay inside the user gesture. Wrapping it in a `setTimeout`
   * or awaiting something first re-breaks the popup blocker.
   */
  viewBrochure: (id: string, fileName: string) => Promise<BrochureViewOutcome>;
  downloadBrochure: (id: string, fileName: string) => Promise<void>;
}

export const toursService: ToursService = {
  list: (signal) => fetchTours(signal),
  get: (id, signal) => orNullOn404(fetchTour(id, signal)),
  create: (payload) => createTour(payload),
  update: (id, payload) => updateTour(id, payload),
  remove: (id) => deleteTour(id),
  replaceItinerary: (id, days) => replaceItinerary(id, days),
  replaceTiers: (id, tiers) => replaceTiers(id, tiers),
  addDeparture: (id, payload) => addDeparture(id, payload),
  updateDeparture: (id, departureId, payload) => updateDeparture(id, departureId, payload),
  removeDeparture: (id, departureId) => removeDeparture(id, departureId),
  linkVendor: (id, payload) => linkVendor(id, payload),
  unlinkVendor: (id, vendorId) => unlinkVendor(id, vendorId),
  uploadBrochure: (id, file) => uploadTourBrochure(id, file),
  removeBrochure: (id) => removeTourBrochure(id),
  // Plain delegation, NOT `async`/`await`: `viewTourBrochure` opens its tab
  // before its first await, and an extra `await` here would push that open out
  // of the click's user gesture and into a blocked popup.
  viewBrochure: (id, fileName) => viewTourBrochure(id, fileName),
  downloadBrochure: (id, fileName) => downloadTourBrochure(id, fileName),
};

/* ── tour website tab (WEBSITE permission) ───────────────────────────────── */

export type {
  AttachTourImageDto,
  AttachTourMediaDto,
  PublishChecklistDto,
  PublishChecklistItemDto,
  ReorderTourImagesDto,
  SetLiveOnWebsiteDto,
  TourImageDto,
  TourMediaRefDto,
  TourWebsiteDto,
  UpdateTourImageDto,
  UpdateTourSlugDto,
  UpdateTourWebsiteDto,
  WebsiteCategory,
};
export { WEBSITE_CATEGORIES } from '@gty/common';

export interface ToursWebsiteService {
  update: (tourId: string, payload: UpdateTourWebsiteDto) => Promise<TourRecord>;
  isSlugAvailable: (tourId: string, slug: string) => Promise<boolean>;
  updateSlug: (tourId: string, payload: UpdateTourSlugDto) => Promise<TourRecord>;
  setCoverImage: (tourId: string, payload: AttachTourMediaDto) => Promise<TourRecord>;
  removeCoverImage: (tourId: string) => Promise<TourRecord>;
  addGalleryImage: (tourId: string, payload: AttachTourImageDto) => Promise<TourRecord>;
  removeGalleryImage: (tourId: string, imageId: string) => Promise<TourRecord>;
  reorderGalleryImages: (tourId: string, imageIds: string[]) => Promise<TourRecord>;
  updateGalleryImage: (tourId: string, imageId: string, altText: string | null) => Promise<TourRecord>;
  setBrochure: (tourId: string, payload: AttachTourMediaDto) => Promise<TourRecord>;
  removeBrochure: (tourId: string) => Promise<TourRecord>;
  getPublishChecklist: (tourId: string) => Promise<PublishChecklistDto>;
  setLive: (tourId: string, live: boolean) => Promise<TourRecord>;
  setPlaces: (tourId: string, placeIds: string[]) => Promise<TourRecord>;
}

export const toursWebsiteService: ToursWebsiteService = {
  update: (tourId, payload) => updateTourWebsite(tourId, payload),
  isSlugAvailable: (tourId, slug) => isTourSlugAvailable(tourId, slug).then((r) => r.available),
  updateSlug: (tourId, payload) => updateTourSlug(tourId, payload),
  setCoverImage: (tourId, payload) => setTourCoverImage(tourId, payload),
  removeCoverImage: (tourId) => removeTourCoverImage(tourId),
  addGalleryImage: (tourId, payload) => addTourGalleryImage(tourId, payload),
  removeGalleryImage: (tourId, imageId) => removeTourGalleryImage(tourId, imageId),
  reorderGalleryImages: (tourId, imageIds) => reorderTourGalleryImages(tourId, { imageIds }),
  updateGalleryImage: (tourId, imageId, altText) =>
    updateTourGalleryImage(tourId, imageId, { altText }),
  setBrochure: (tourId, payload) => setTourWebsiteBrochure(tourId, payload),
  removeBrochure: (tourId) => removeTourWebsiteBrochure(tourId),
  getPublishChecklist: (tourId) => fetchTourPublishChecklist(tourId),
  setLive: (tourId, live) => setTourLiveOnWebsite(tourId, { live }),
  setPlaces: (tourId, placeIds) => setTourPlaces(tourId, { placeIds }),
};

/* ── website: places ─────────────────────────────────────────────────────── */

export type { CreatePlaceDto, PlaceDto, UpdatePlaceDto } from '@gty/common';

export interface PlacesService {
  list: (signal?: AbortSignal) => Promise<readonly PlaceDto[]>;
  get: (id: string, signal?: AbortSignal) => Promise<PlaceDto | null>;
  create: (payload: CreatePlaceDto) => Promise<PlaceDto>;
  update: (id: string, payload: UpdatePlaceDto) => Promise<PlaceDto>;
  reorder: (placeIds: string[]) => Promise<PlaceDto[]>;
  setImage: (id: string, payload: AttachTourMediaDto) => Promise<PlaceDto>;
  removeImage: (id: string) => Promise<PlaceDto>;
  remove: (id: string) => Promise<void>;
}

export const placesService: PlacesService = {
  list: (signal) => fetchPlaces(signal),
  get: (id, signal) => orNullOn404(fetchPlace(id, signal)),
  create: (payload) => createPlace(payload),
  update: (id, payload) => updatePlace(id, payload),
  reorder: (placeIds) => reorderPlaces({ placeIds }),
  setImage: (id, payload) => setPlaceImage(id, payload),
  removeImage: (id) => removePlaceImage(id),
  remove: (id) => deletePlace(id).then(() => undefined),
};

/* ── website: site images, site settings ─────────────────────────────────── */

export type {
  AttachSiteImageDto,
  ReorderSiteImagesDto,
  SiteImageDto,
  SiteImageSlot,
  UpdateSiteImageDto,
  UpdateWebsiteSettingsDto,
  WebsiteSettingsDto,
};
export { SITE_IMAGE_SLOTS, SITE_IMAGE_SLOT_RULES } from '@gty/common';

export interface SiteContentService {
  listImages: (slot?: SiteImageSlot, signal?: AbortSignal) => Promise<SiteImageDto[]>;
  attachImage: (payload: AttachSiteImageDto) => Promise<SiteImageDto>;
  updateImage: (id: string, payload: UpdateSiteImageDto) => Promise<SiteImageDto>;
  removeImage: (id: string) => Promise<void>;
  reorderImages: (slot: SiteImageSlot, imageIds: string[]) => Promise<SiteImageDto[]>;
  getSettings: (signal?: AbortSignal) => Promise<WebsiteSettingsDto>;
  updateSettings: (payload: UpdateWebsiteSettingsDto) => Promise<WebsiteSettingsDto>;
}

export const siteContentService: SiteContentService = {
  listImages: (slot, signal) => fetchSiteImages(slot, signal),
  attachImage: (payload) => attachSiteImage(payload),
  updateImage: (id, payload) => updateSiteImage(id, payload),
  removeImage: (id) => removeSiteImage(id).then(() => undefined),
  reorderImages: (slot, imageIds) => reorderSiteImages({ slot, imageIds }),
  getSettings: (signal) => fetchWebsiteSettings(signal),
  updateSettings: (payload) => updateWebsiteSettings(payload),
};

/* ── bookings ──────────────────────────────────────────────────────────────── */

export type {
  AddBookingNoteDto,
  BookingDeletionPreviewDto,
  BookingEditLogEntryDto,
  BookingNoteDto,
  CancelBookingDto,
  CancelBookingResultDto,
  CancellationSettlement,
  ChangeBookingCustomerDto,
  CreateBookingDto,
  DeletedBookingArchiveDto,
  DeleteBookingDto,
  DeleteBookingResultDto,
  EditBookingDto,
  EditBookingPassengerDto,
  EditBookingPreviewDto,
  EditBookingResultDto,
  EditBookingSettlementDto,
  UpdateBookingStatusDto,
  BookingPassengerInputDto,
  ReviseBookingValueDto,
} from '@gty/common';
export { DELETE_BOOKING_MIN_REASON_LENGTH } from '@gty/common';

export interface BookingsService {
  list: (signal?: AbortSignal) => Promise<readonly BookingRecord[]>;
  get: (id: string, signal?: AbortSignal) => Promise<BookingRecord | null>;
  create: (payload: CreateBookingDto) => Promise<BookingRecord>;
  /**
   * Mark the trip completed.
   *
   * CONFIRMED is deliberately not available — the server advances a booking to
   * it when payment crosses the deposit threshold. CANCELLED is no longer
   * accepted here either: cancelling settles money, so it has its own action.
   */
  setStatus: (id: string, payload: UpdateBookingStatusDto) => Promise<BookingRecord>;
  /**
   * Revise the agreed value — the only way `total` ever moves.
   *
   * Separate from `setStatus` because it is a different authority: INVOICE_
   * GENERATOR:FULL (Owner, Admin, Accounts), not BOOKING_MANAGEMENT. `reason` is
   * required by the contract, not optional as it is on a status change.
   */
  reviseValue: (id: string, payload: ReviseBookingValueDto) => Promise<BookingRecord>;
  /**
   * "Mark trip" — append an internal staff note.
   *
   * Resolves to the refreshed booking, so the caller replaces its cache entry
   * rather than splicing `notes` by hand.
   */
  addNote: (id: string, payload: AddBookingNoteDto) => Promise<BookingRecord>;
  /**
   * Cancel the booking AND settle the money, in one request.
   *
   * Separate from `setStatus` because it is a different authority
   * (PAYMENTS_RECEIPTS:FULL — Owner, Admin, Accounts) and because a status enum
   * has nowhere to put the settlement choice. Resolves to the refreshed booking
   * plus the refs of whatever document the chosen path produced.
   */
  cancel: (id: string, payload: CancelBookingDto) => Promise<CancelBookingResultDto>;
  /**
   * Live preview for the Edit booking screen — no write, same inputs the
   * save would apply. Called on every form change.
   */
  editPreview: (
    id: string,
    payload: Pick<EditBookingDto, 'passengers' | 'total' | 'extraCharges' | 'discount' | 'tourDateId'>,
  ) => Promise<EditBookingPreviewDto>;
  /**
   * THE FULL "EDIT BOOKING" SAVE — passengers, pricing, departure, all in
   * one request. BOOKING_MANAGEMENT:FULL, same authority as `create`.
   */
  edit: (id: string, payload: EditBookingDto) => Promise<EditBookingResultDto>;
  /** Read-only "Edit history" list on the booking detail screen. */
  editHistory: (id: string) => Promise<BookingEditLogEntryDto[]>;
  /** Change the customer a booking belongs to. Owner only on the server. */
  changeCustomer: (id: string, payload: ChangeBookingCustomerDto) => Promise<void>;
  /**
   * Exactly what "Delete booking" is about to erase — read before the Owner
   * can type the booking number. Owner-only on the server.
   */
  getDeletionPreview: (id: string) => Promise<BookingDeletionPreviewDto>;
  /** PERMANENTLY ERASE A BOOKING. Owner only, any status, no undo. */
  deleteBooking: (id: string, payload: DeleteBookingDto) => Promise<DeleteBookingResultDto>;
}

export const bookingsService: BookingsService = {
  list: (signal) => fetchBookings(signal),
  get: (id, signal) => orNullOn404(fetchBooking(id, signal)),
  create: (payload) => createBooking(payload),
  setStatus: (id, payload) => updateBookingStatus(id, payload),
  reviseValue: (id, payload) => reviseBookingValue(id, payload),
  addNote: (id, payload) => addBookingNote(id, payload),
  cancel: (id, payload) => cancelBooking(id, payload),
  editPreview: (id, payload) => previewEditBooking(id, payload),
  edit: (id, payload) => editBooking(id, payload),
  editHistory: (id) => getBookingEditHistory(id),
  changeCustomer: (id, payload) => changeBookingCustomer(id, payload),
  getDeletionPreview: (id) => getBookingDeletionPreview(id),
  deleteBooking: (id, payload) => deleteBooking(id, payload),
};

/* ── expenses ──────────────────────────────────────────────────────────────── */

export type {
  AddExpensePaymentDto,
  BookingProfitDto,
  CreateExpenseDto,
  DepartureProfitDto,
  ExpenseDto,
  ExpenseListDto,
  ExpenseListFiltersDto,
  ExpenseSummaryDto,
  PayablesAgingDto,
  ProfitBasis,
  UpdateExpenseDto,
  VoidExpenseDto,
} from '@gty/common';
export {
  EXPENSE_CATEGORIES,
  EXPENSE_CATEGORIES_BY_SCOPE,
  EXPENSE_CATEGORY_LABELS,
  EXPENSE_SCOPES,
  EXPENSE_VOID_MIN_REASON_LENGTH,
} from '@gty/common';
export type { ExpenseCategory, ExpenseScope, ExpensePaymentState, ExpenseStatus } from '@gty/common';

/**
 * DEMO-MODE SWAP HAPPENS HERE, and only here.
 *
 * Every method checks `EXPENSES_DEMO_ENABLED` first. In a production build
 * that constant is the literal `false` (see `demo-mode.ts`'s own note), so
 * every branch below it — including the `import('./demo-mode')` itself — is
 * dead code Rollup removes; the real `fetchExpenses`/`createExpense`/etc.
 * calls are all that ships. Swapping at THIS layer, rather than inside
 * `ExpensesPage` itself, is what lets the booking/departure profit sections
 * and the dashboard panel get demo data for free with no duplicated logic.
 */
export interface ExpensesService {
  list: (filters: ExpenseListFiltersDto, signal?: AbortSignal) => Promise<ExpenseListDto>;
  get: (id: string, signal?: AbortSignal) => Promise<ExpenseDto | null>;
  summary: (from?: string, to?: string, basis?: ProfitBasis, signal?: AbortSignal) => Promise<ExpenseSummaryDto>;
  payablesAging: (signal?: AbortSignal) => Promise<PayablesAgingDto>;
  vendorSuggestions: (q: string, signal?: AbortSignal) => Promise<string[]>;
  create: (payload: CreateExpenseDto) => Promise<ExpenseDto>;
  update: (id: string, payload: UpdateExpenseDto) => Promise<ExpenseDto>;
  void: (id: string, payload: VoidExpenseDto) => Promise<ExpenseDto>;
  addPayment: (id: string, payload: AddExpensePaymentDto) => Promise<ExpenseDto>;
  deletePayment: (id: string, paymentId: string) => Promise<ExpenseDto>;
  bookingProfit: (bookingId: string, signal?: AbortSignal) => Promise<BookingProfitDto | null>;
  departureProfit: (tourId: string, departureId: string, signal?: AbortSignal) => Promise<DepartureProfitDto | null>;
  /** Not available in demo mode — see `ExpensesPage`'s own Export button, which disables rather than calling this. */
  exportWorkbook: (from: string, to: string) => Promise<void>;
}

export const expensesService: ExpensesService = {
  list: async (filters, signal) => {
    if (EXPENSES_DEMO_ENABLED) return (await import('../expenses/demo-mode')).demoList(filters);
    return fetchExpenses(filters, signal);
  },
  get: async (id, signal) => {
    if (EXPENSES_DEMO_ENABLED) return (await import('../expenses/demo-mode')).demoGet(id);
    return orNullOn404(fetchExpense(id, signal));
  },
  summary: async (from, to, basis, signal) => {
    if (EXPENSES_DEMO_ENABLED) return (await import('../expenses/demo-mode')).demoSummary();
    return fetchExpenseSummary(from, to, basis, signal);
  },
  payablesAging: async (signal) => {
    if (EXPENSES_DEMO_ENABLED) return (await import('../expenses/demo-mode')).demoPayablesAging();
    return fetchPayablesAging(signal);
  },
  vendorSuggestions: async (q, signal) => {
    if (EXPENSES_DEMO_ENABLED) return [];
    return fetchVendorSuggestions(q, signal);
  },
  create: async (payload) => {
    if (EXPENSES_DEMO_ENABLED) return (await import('../expenses/demo-mode')).demoCreate(payload);
    return createExpense(payload);
  },
  update: async (id, payload) => {
    if (EXPENSES_DEMO_ENABLED) return (await import('../expenses/demo-mode')).demoUpdate(id, payload);
    return updateExpense(id, payload);
  },
  void: async (id, payload) => {
    if (EXPENSES_DEMO_ENABLED) return (await import('../expenses/demo-mode')).demoVoid(id, payload);
    return voidExpense(id, payload);
  },
  addPayment: async (id, payload) => {
    if (EXPENSES_DEMO_ENABLED) return (await import('../expenses/demo-mode')).demoAddPayment(id, payload);
    return addExpensePayment(id, payload);
  },
  deletePayment: async (id, paymentId) => {
    if (EXPENSES_DEMO_ENABLED) return (await import('../expenses/demo-mode')).demoDeletePayment(id, paymentId);
    return deleteExpensePayment(id, paymentId);
  },
  bookingProfit: async (bookingId, signal) => {
    if (EXPENSES_DEMO_ENABLED) return (await import('../expenses/demo-mode')).demoBookingProfit(bookingId);
    return orNullOn404(fetchBookingProfit(bookingId, signal));
  },
  departureProfit: async (tourId, departureId, signal) => {
    if (EXPENSES_DEMO_ENABLED) return (await import('../expenses/demo-mode')).demoDepartureProfit(departureId);
    return orNullOn404(fetchDepartureProfit(tourId, departureId, signal));
  },
  exportWorkbook: async (from, to) => {
    if (EXPENSES_DEMO_ENABLED) {
      throw new Error('Export is not available in demo mode.');
    }
    return downloadExpensesExport(from, to, `expenses-export-${from}-to-${to}.xlsx`);
  },
};

/** The Owner's read-only "Deleted bookings" list — Settings/Audit log. */
export interface DeletedBookingsService {
  list: () => Promise<readonly DeletedBookingArchiveDto[]>;
}

export const deletedBookingsService: DeletedBookingsService = {
  list: () => listDeletedBookings(),
};

/* ── invoices ──────────────────────────────────────────────────────────────── */

export type { UpdateInvoiceDto, DocumentEditLogEntryDto } from '@gty/common';

export interface InvoicesService {
  list: (signal?: AbortSignal) => Promise<readonly InvoiceRecord[]>;
  get: (id: string, signal?: AbortSignal) => Promise<InvoiceRecord | null>;
  /**
   * Header figures for the list screen.
   *
   * Was a hardcoded `INVOICE_SUMMARY` constant. It is a server aggregate now, so
   * the header cannot disagree with the rows beneath it.
   */
  summary: (signal?: AbortSignal) => Promise<InvoiceSummaryDto>;
  /**
   * The invoice a booking has, or `null` if it has not reached FULLY_PAID.
   *
   * `null` is the NORMAL state for most bookings, not a failure — the invoice is
   * raised by the payment that settles the balance. Hence `orNullOn404`.
   */
  forBooking: (bookingId: string, signal?: AbortSignal) => Promise<InvoiceRecord | null>;
  /** Edit in place. One record, no versions; editable after download. */
  update: (id: string, payload: UpdateInvoiceDto) => Promise<InvoiceRecord>;
  /** Audit trail only — never surfaced as selectable versions. */
  edits: (id: string, signal?: AbortSignal) => Promise<readonly DocumentEditLogEntryDto[]>;
}

export const invoicesService: InvoicesService = {
  list: (signal) => fetchInvoices(signal),
  get: (id, signal) => orNullOn404(fetchInvoice(id, signal)),
  summary: (signal) => fetchInvoiceSummary(signal),
  forBooking: (bookingId, signal) => orNullOn404(fetchInvoiceForBooking(bookingId, signal)),
  update: (id, payload) => updateInvoice(id, payload),
  edits: (id, signal) => fetchInvoiceEdits(id, signal),
};

/* ── vouchers ──────────────────────────────────────────────────────────────── */

export type { UpdateVoucherDto, VoucherDto } from '@gty/common';

export interface VouchersService {
  list: (signal?: AbortSignal) => Promise<readonly VoucherWithInvoice[]>;
  get: (id: string, signal?: AbortSignal) => Promise<VoucherWithInvoice | null>;
  /**
   * The voucher a booking has, or `null` before the token payment lands.
   *
   * There is deliberately no `create`. A voucher is raised inside the
   * transaction of the payment that reaches TOKEN_PAID — the server has no
   * POST /vouchers at all — so a "Generate voucher" affordance would be a button
   * for a route that does not exist, and would allow a document for a booking
   * nobody has paid for.
   */
  forBooking: (bookingId: string, signal?: AbortSignal) => Promise<VoucherWithInvoice | null>;
  /** Edit in place. Refused with 409 DOCUMENT_LOCKED once the booking completes. */
  update: (id: string, payload: UpdateVoucherDto) => Promise<VoucherDto>;
  /** Audit trail only — never surfaced as selectable versions. */
  edits: (id: string, signal?: AbortSignal) => Promise<readonly DocumentEditLogEntryDto[]>;
}

export const vouchersService: VouchersService = {
  list: (signal) => fetchVouchers(signal),
  get: (id, signal) => orNullOn404(fetchVoucher(id, signal)),
  forBooking: (bookingId, signal) => orNullOn404(fetchVoucherForBooking(bookingId, signal)),
  update: (id, payload) => updateVoucher(id, payload),
  edits: (id, signal) => fetchVoucherEdits(id, signal),
};

/* ── credit notes ──────────────────────────────────────────────────────────── */

export type { CreditNoteDto, CreditNoteLineDto, CreditNoteStatus } from '@gty/common';
export type { CreateCreditNoteDto, CreditNoteLineInputDto } from '@gty/common';

export interface CreditNotesService {
  list: (signal?: AbortSignal) => Promise<readonly CreditNoteDto[]>;
  get: (id: string, signal?: AbortSignal) => Promise<CreditNoteDto | null>;
  create: (payload: CreateCreditNoteDto) => Promise<CreditNoteDto>;
}

export const creditNotesService: CreditNotesService = {
  list: (signal) => fetchCreditNotes(signal),
  get: (id, signal) => orNullOn404(fetchCreditNote(id, signal)),
  create: (payload) => createCreditNote(payload),
};

/* ── payments & receipts ───────────────────────────────────────────────────── */

export type { PaymentDto, PaymentMode, PaymentStatus, PaymentSummaryDto } from '@gty/common';

export type { RecordPaymentDto, LedgerEntryDto, PaymentLedgerDto, ReceiptDto } from '@gty/common';

export type { ChequeAction, ChequeActionRequestDto } from '../payments/cheque-actions';

export interface PaymentsService {
  list: (signal?: AbortSignal) => Promise<readonly PaymentDto[]>;
  summary: (signal?: AbortSignal) => Promise<PaymentSummaryDto>;
  /** Every receipt across every booking of one customer, newest first. */
  forCustomer: (customerId: string, signal?: AbortSignal) => Promise<readonly PaymentDto[]>;
  /**
   * Recalculates the booking's paid total, may auto-confirm it, and may raise
   * the voucher or the invoice — all inside one server transaction.
   *
   * `idempotencyKey` should be minted once per dialog session, so a retry of the
   * SAME submission is de-duplicated while a genuinely separate second payment
   * of identical shape is not.
   */
  record: (payload: RecordPaymentDto, idempotencyKey?: string) => Promise<PaymentDto>;
  /**
   * Move an IN_CLEARING cheque to CLEARED or to BOUNCED.
   *
   * The ONLY two writes against an existing payment in the whole API, and both
   * are one-way: the server accepts them solely from IN_CLEARING, so a second
   * call — a double-click, or a colleague who got there first — is refused with
   * a 409 rather than applied twice. `reason` is mandatory and goes to the
   * audit trail.
   *
   * Resolves to the raw reply: read it with `readChequeActionOutcome`, and take
   * the money figures from a refreshed `ledger()` rather than from here.
   */
  clearCheque: (paymentId: string, payload: ChequeActionRequestDto) => Promise<unknown>;
  bounceCheque: (paymentId: string, payload: ChequeActionRequestDto) => Promise<unknown>;
  /** "Void payment (entered by mistake)" — item 3. Owner only, server-enforced. */
  void: (paymentId: string, payload: VoidPaymentDto) => Promise<VoidPaymentResultDto>;
  /** Read-only. Running balances come from the server; never recompute them. */
  ledger: (bookingId: string, signal?: AbortSignal) => Promise<PaymentLedgerDto>;
  /** Immutable. There is no update counterpart anywhere in the API. */
  receipt: (idOrRef: string, signal?: AbortSignal) => Promise<ReceiptDto>;
}

export const paymentsService: PaymentsService = {
  list: (signal) => fetchPayments(signal),
  summary: (signal) => fetchPaymentSummary(signal),
  forCustomer: (customerId, signal) => fetchPaymentsForCustomer(customerId, signal),
  record: (payload, idempotencyKey) => recordPayment(payload, idempotencyKey),
  clearCheque: (paymentId, payload) => clearChequePayment(paymentId, payload),
  bounceCheque: (paymentId, payload) => bounceChequePayment(paymentId, payload),
  void: (paymentId, payload) => voidPayment(paymentId, payload),
  ledger: (bookingId, signal) => fetchPaymentLedger(bookingId, signal),
  receipt: (idOrRef, signal) => fetchReceipt(idOrRef, signal),
};

/* ── share links ("Send via WhatsApp") ─────────────────────────────────────── */

export type { ShareDocumentKind, ShareLinkDto, ShareLinkSummaryDto } from '../documents/share-link';

export interface ShareLinksService {
  /**
   * Mint a link for one document and get its token back.
   *
   * NOT idempotent in the strict sense, and not claimed to be: the server
   * best-effort reuses an existing live link for the same document rather than
   * rendering a second PDF, but two clicks within milliseconds can still produce
   * two rows. That is an accepted, bounded outcome for a convenience action —
   * both links point at the same document and both are revocable.
   */
  create: (kind: ShareDocumentKind, documentId: string) => Promise<ShareLinkDto>;
  /**
   * The links a document already has, WITHOUT their tokens.
   *
   * For the revoke affordance only. A URL that has been sent cannot be recovered
   * from here by design — share again instead, which mints a fresh link and
   * leaves the old one revocable.
   */
  list: (
    kind: ShareDocumentKind,
    documentId: string,
    signal?: AbortSignal,
  ) => Promise<readonly ShareLinkSummaryDto[]>;
  /** Revoke one link. Takes the document too, so the server can verify the pair. */
  revoke: (kind: ShareDocumentKind, documentId: string, shareLinkId: string) => Promise<void>;
}

export const shareLinksService: ShareLinksService = {
  create: (kind, documentId) => createShareLink(kind, documentId),
  list: (kind, documentId, signal) => fetchShareLinks(kind, documentId, signal),
  revoke: (kind, documentId, shareLinkId) => revokeShareLink(kind, documentId, shareLinkId),
};

/* ── reports & analytics ───────────────────────────────────────────────────── */

export type {
  AvailableReportMonthDto,
  DestinationRevenueDto,
  FunnelStageDto,
  ReportsSummaryDto,
  RevenueMonthDto,
  StaffPerformanceDto,
} from '@gty/common';

export interface ReportsService {
  summary: (signal?: AbortSignal) => Promise<ReportsSummaryDto>;
  months: (signal?: AbortSignal) => Promise<AvailableReportMonthDto[]>;
  downloadMonthlyWorkbook: (month: string) => Promise<void>;
  /** The monthly overview table (Part C) — same shared data the Dashboard carousel uses. */
  monthlyOverview: (signal?: AbortSignal) => Promise<DashboardMonthlyOverviewDto>;
  downloadMonthlyOverview: () => Promise<void>;
}

export const reportsService: ReportsService = {
  summary: (signal) => fetchReportsSummary(signal),
  months: (signal) => fetchReportMonths(signal),
  downloadMonthlyWorkbook: (month) => downloadMonthlyReport(month, `monthly-report-${month}.xlsx`),
  monthlyOverview: (signal) => fetchMonthlyOverview(signal),
  downloadMonthlyOverview: () => downloadMonthlyOverview(`monthly-overview-${new Date().toISOString().slice(0, 10)}.xlsx`),
};

/* ── settings & roles ──────────────────────────────────────────────────────── */

export type {
  BankDetailsDto,
  InvoiceSettingsDto,
  PermissionMatrixDto,
  PermissionCellUpdateDto,
  TeamMemberDto,
  UpdateInvoiceSettingsDto,
} from '@gty/common';

export interface SettingsService {
  getMatrix: (signal?: AbortSignal) => Promise<PermissionMatrixDto>;
  saveMatrix: (changes: PermissionCellUpdateDto[]) => Promise<PermissionMatrixDto>;
  getTeam: (signal?: AbortSignal) => Promise<readonly TeamMemberDto[]>;
  createUser: (payload: CreateUserDto) => Promise<TeamMemberDto>;
  updateUser: (id: string, payload: UpdateUserDto) => Promise<TeamMemberDto>;
  /**
   * Bank Details / Terms & Conditions / Booking Branch — read live by every
   * invoice, edited Owner-only from Settings -> Invoice — Bank Details & Terms.
   */
  getInvoiceConfig: (signal?: AbortSignal) => Promise<InvoiceSettingsDto>;
  saveInvoiceConfig: (payload: UpdateInvoiceSettingsDto) => Promise<InvoiceSettingsDto>;
  /**
   * Upload the payment QR image printed on invoices raised from now on.
   *
   * Separate from `saveInvoiceConfig` because it is a different request shape
   * (multipart) with different failure modes (wrong type, too big, magic-byte
   * mismatch) — folding it into the form save would mean one Save button that
   * either does or does not carry a file, and one error slot for two unrelated
   * kinds of rejection.
   */
  uploadQrImage: (file: File) => Promise<InvoiceSettingsDto>;
}

export const settingsService: SettingsService = {
  getMatrix: (signal) => fetchPermissionMatrix(signal),
  saveMatrix: (changes) => updatePermissionMatrix({ changes }),
  getTeam: (signal) => fetchTeam(signal),
  createUser: (payload) => createUser(payload),
  updateUser: (id, payload) => updateUser(id, payload),
  getInvoiceConfig: (signal) => fetchInvoiceSettings(signal),
  saveInvoiceConfig: (payload) => updateInvoiceSettings(payload),
  uploadQrImage: (file) => uploadInvoiceQrImage(file),
};

/* ── website media (Cloudflare R2) ───────────────────────────────────────── */

export type { CreateUploadUrlDto, MediaPurpose, UploadUrlDto };

export interface MediaService {
  createUploadUrl: (input: CreateUploadUrlDto) => Promise<UploadUrlDto>;
}

export const mediaService: MediaService = {
  createUploadUrl: (input) => createMediaUploadUrl(input),
};

/* ── shell chrome ──────────────────────────────────────────────────────────── */

/**
 * A tray row, as the TopBar draws it.
 *
 * `icon` and `tone` are PRESENTATION and are derived here from the server's
 * `kind` + `overdue` — they are not on the wire. The API says what a
 * notification IS; how red it looks is the design's business, and shipping the
 * colour from the server is how a palette change becomes a backend deploy.
 */
export interface ShellNotification {
  id: string;
  icon: IconName;
  tone: 'danger' | 'warning' | 'neutral' | 'success';
  title: string;
  detail: string;
  unread: boolean;
  /** In-app route to open on click. */
  to?: string;
}

/**
 * THE FIXTURES ARE GONE.
 *
 * This used to be `SHELL_NOTIFICATIONS`, a hardcoded array of four invented
 * entries — an overdue invoice for a customer who does not exist, a departure
 * for a booking that does not exist, "2 new leads from Instagram", and a
 * receipt. That array WAS the reported bug: it could not be anything but stale,
 * because it was never about the database at all. Same shape of defect as the
 * old `DASHBOARD_SUMMARY` constant, and removed the same way.
 *
 * `GET /api/notifications` now derives exactly two kinds — upcoming departures
 * and due balances — from live rows on every read.
 */
function toShellNotification(dto: NotificationDto): ShellNotification {
  const tone: ShellNotification['tone'] =
    dto.kind === 'PAYMENT_DUE' ? (dto.overdue ? 'danger' : 'warning') : 'neutral';
  return {
    id: dto.id,
    icon: dto.kind === 'PAYMENT_DUE' ? 'alert' : 'tours',
    tone,
    title: dto.title,
    detail: dto.detail,
    unread: dto.unread,
    to: dto.to,
  };
}

/** `ActivityCategory` -> the icon the bell dropdown and the feed page both use. */
/** `ActivityCategory` -> icon, shared by the bell dropdown and the Notifications page. */
export const CATEGORY_ICON: Readonly<Record<ActivityCategory, IconName>> = Object.freeze({
  LEADS: 'leads',
  CUSTOMERS: 'customers',
  BOOKINGS: 'bookings',
  PAYMENTS: 'payments',
  DOCUMENTS: 'invoices',
  TOURS: 'tours',
  EXPENSES: 'expenses',
  ADMIN: 'settings',
});

function toShellActivity(dto: ActivityEventDto): ShellNotification {
  return {
    id: dto.id,
    icon: CATEGORY_ICON[dto.category],
    tone: 'neutral',
    title: dto.message,
    detail: dto.actorName,
    unread: dto.unread,
    to: dto.to,
  };
}

export type { NotificationDto, NotificationListDto };

/** The two derived reminders, for the Notifications page's own "Needs attention" block. */
export const notificationsService = {
  list: (signal?: AbortSignal): Promise<NotificationListDto> => fetchNotifications(signal),
};

export interface ShellChrome {
  /** The two derived reminders (departure soon, payment due) — live alerts, not history. */
  needsAttention: readonly ShellNotification[];
  /** The latest stored events, newest first — "what happened", capped for the dropdown. */
  recentActivity: readonly ShellNotification[];
  /** Combined — needs-attention items plus stored events the user has not read. */
  unreadCount: number;
}

const BELL_RECENT_ACTIVITY_LIMIT = 8;

export const shellService = {
  /**
   * Chrome for the shell.
   *
   * THE SIDEBAR COUNTER PILLS ARE NO LONGER RETURNED FROM HERE.
   *
   * They used to be `{ leads: 12, payments: 3 }` — a hardcoded fixture, the last
   * survivor of the same class as `SHELL_NOTIFICATIONS` and `DASHBOARD_SUMMARY`.
   * Against a purged database it advertised twelve leads that do not exist. They
   * are now derived in `AppShell` from the leads list and the payments summary,
   * both of which are endpoints that already existed — no new route was invented
   * to serve a counter.
   *
   * Two independent reads, combined here: the derived reminders (`GET
   * /notifications`) and the stored activity feed (`GET
   * /notifications/feed`), because they answer different questions — "what
   * needs doing" versus "what happened" — and the owner's own spec keeps them
   * visually separate in the dropdown.
   */
  get: async (signal?: AbortSignal): Promise<ShellChrome> => {
    const [reminders, feed] = await Promise.all([
      fetchNotifications(signal),
      fetchActivityFeed({ page: 1 }, signal),
    ]);
    return {
      needsAttention: reminders.items.map(toShellNotification),
      recentActivity: feed.items.slice(0, BELL_RECENT_ACTIVITY_LIMIT).map(toShellActivity),
      unreadCount: reminders.unreadCount + feed.unreadCount,
    };
  },

  /** Mark all of the current user's notifications read — both reminders and stored events. */
  markAllRead: (): Promise<MarkAllReadResultDto> => markAllNotificationsRead(),
};

/* ── activity feed (the Notifications page, the Dashboard panel) ────────────── */

export type { ActivityCategory, ActivityEventDto, ActivityEventType, ActivityFeedDto, AvailableActivityMonthDto };

export interface ActivityService {
  feed: (query: ActivityFeedQuery, signal?: AbortSignal) => Promise<ActivityFeedDto>;
  months: (signal?: AbortSignal) => Promise<AvailableActivityMonthDto[]>;
  markRead: (id: string) => Promise<MarkEventReadResultDto>;
  markAllRead: () => Promise<MarkAllReadResultDto>;
}

export const activityService: ActivityService = {
  feed: (query, signal) => fetchActivityFeed(query, signal),
  months: (signal) => fetchActivityMonths(signal),
  markRead: (id) => markActivityEventRead(id),
  markAllRead: () => markAllNotificationsRead(),
};

/**
 * GLOBAL SEARCH.
 *
 * A thin pass-through, and deliberately so — there is nothing to reshape. The
 * server already returns the four groups in the order the dropdown renders them
 * and already caps them, so a mapping layer here would only be a second place
 * for the grouping to be decided.
 *
 * NOT a client-side filter over an already-loaded list: the four modules are
 * never all resident in the cache at once, and a role that cannot view leads
 * must not be able to search them. Both facts make this a server question.
 */
export const searchService = {
  global: (q: string, signal?: AbortSignal): Promise<GlobalSearchResultsDto> =>
    searchGlobal(q, signal),
};
